/* Avoriell: Bundle & save picker, "Complete the set" and cart upsells, on top of the theme's cart components. */
(() => {
  const config = window.avoriell || {};
  const strings = config.strings || {};
  const routes = config.routes || {};

  /* ---------- Helpers ---------- */
  const formatMoney = (cents) => {
    const format = (config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = Number(cents) || 0;
    const withDelimiters = (precision, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(precision).split('.');
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(0);
        case 'amount_with_comma_separator':
          return withDelimiters(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(0, '.', ',');
        default:
          return withDelimiters(2);
      }
    });
  };

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template || '');

  const shortTitle = (title = '', vendor = '') => {
    if (vendor && title.toLowerCase().startsWith(vendor.toLowerCase()) && title.length > vendor.length) {
      return title.slice(vendor.length).trim() || title;
    }
    return title;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    const src = url.startsWith('//') ? `https:${url}` : url;
    return /[?&]width=/.test(src) ? src : `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };

  const el = (tag, attrs = {}, children = []) => {
    const node = document.createElement(tag);
    Object.entries(attrs).forEach(([key, value]) => {
      if (value === null || value === undefined || value === false) return;
      if (key === 'text') node.textContent = value;
      else node.setAttribute(key, value);
    });
    children.filter(Boolean).forEach((child) => node.append(child));
    return node;
  };

  const parseJSON = (root, selector, fallback) => {
    const script = root.querySelector(selector);
    if (!script) return fallback;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return fallback;
    }
  };

  /* ---------- Cart ---------- */
  const cartRequest = async (url, body) => {
    const drawer = document.querySelector('cart-drawer');
    const sections = drawer ? drawer.getSectionsToRender().map((section) => section.id) : [];
    const response = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections, sections_url: window.location.pathname })
    });
    const state = await response.json();
    if (!response.ok || state.status) throw new Error(state.description || state.message || strings.cartError);

    if (document.querySelector('cart-items')) {
      window.location.reload();
      return state;
    }
    if (drawer && state.sections) {
      drawer.classList.remove('is-empty');
      drawer.renderContents(state);
    }
    if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'avoriell', cartData: state });
    return state;
  };

  const addToCart = (items) => cartRequest(routes.cartAdd, { items });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-av-cart-set]');
    if (!button) return;
    button.disabled = true;
    try {
      await cartRequest(routes.cartChange, { id: button.dataset.key, quantity: Number(button.dataset.quantity) });
    } catch (error) {
      button.disabled = false;
      window.alert(error.message);
    }
  });

  /* ---------- Recommendations (shared by Complete the set and cart upsells) ---------- */
  const recommendationCache = new Map();

  const fetchRecommendations = (productId, intent) => {
    const key = `${productId}:${intent}`;
    if (!recommendationCache.has(key)) {
      recommendationCache.set(
        key,
        fetch(`${routes.recommendations}.json?product_id=${productId}&limit=10&intent=${intent}`)
          .then((response) => (response.ok ? response.json() : { products: [] }))
          .then((data) => data.products || [])
          .catch(() => [])
      );
    }
    return recommendationCache.get(key);
  };

  const recommendationsFor = async (productId) => {
    const [complementary, related] = await Promise.all([
      fetchRecommendations(productId, 'complementary'),
      fetchRecommendations(productId, 'related')
    ]);
    return [...complementary, ...related];
  };

  const normaliseProduct = (product) => {
    if ('variant_id' in product) return product;
    const variants = product.variants || [];
    return {
      id: product.id,
      variant_id: variants[0]?.id,
      title: product.title,
      vendor: product.vendor,
      type: product.type || '',
      price: product.price,
      available: product.available,
      single: variants.length === 1,
      tags: product.tags || [],
      url: product.url,
      image: product.featured_image || ''
    };
  };

  const hasAnyTag = (tags, list) => {
    const lower = (tags || []).map((tag) => String(tag).trim().toLowerCase());
    return (list || []).some((tag) => tag.trim() && lower.includes(tag.trim().toLowerCase()));
  };

  const tierFor = (count) =>
    (config.bundles?.tiers || [])
      .filter((tier) => Number(tier.qty) <= count)
      .reduce((best, tier) => Math.max(best, Number(tier.pct)), 0);

  const discounted = (cents, qty, pct) => {
    const full = cents * qty;
    const total = full - Math.round((full * pct) / 100);
    return { full, total, each: Math.floor(total / qty) };
  };

  /* ---------- Bundle & save picker ---------- */
  class BundlePicker extends HTMLElement {
    connectedCallback() {
      this.price = Number(this.dataset.price);
      this.addEventListener('change', () => this.update());
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data.sectionId !== this.dataset.sectionId || !data.variant) return;
        this.price = data.variant.price;
        this.update();
      });
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    update() {
      const selected = this.querySelector('input:checked');
      this.querySelectorAll('.av-bundle__option').forEach((option) => {
        const pct = Number(option.dataset.pct);
        const prices = discounted(this.price, Number(option.dataset.qty), pct);
        option.classList.toggle('is-selected', option.contains(selected));
        option.querySelector('[data-tier-total]').textContent = formatMoney(prices.total);
        const full = option.querySelector('[data-tier-full]');
        if (full) full.textContent = formatMoney(prices.full);
        const note = option.querySelector('[data-tier-note]');
        if (note && pct > 0) note.textContent = fill(strings.each, { percent: pct, price: formatMoney(prices.each) });
      });
      const input = document.getElementById(`Quantity-${this.dataset.sectionId}`);
      if (input && selected) {
        input.value = selected.value;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  if (!customElements.get('av-bundle-picker')) customElements.define('av-bundle-picker', BundlePicker);

  /* ---------- Complete the set ---------- */
  class CompleteSet extends HTMLElement {
    connectedCallback() {
      if (!config.bundles?.on || !config.bundles?.routineOn) return;
      this.anchor = parseJSON(this, '[data-set-anchor]', null);
      if (this.anchor) this.load();
    }

    async load() {
      const anchor = this.anchor;
      const related = (await recommendationsFor(anchor.id))
        .map(normaliseProduct)
        .filter((product) => hasAnyTag(product.tags, [this.dataset.category || '']));
      const excluded = config.bundles.routineExcluded || [];
      const limit = anchor.price * 2 + 2000;
      const candidates = [...related, ...parseJSON(this, '[data-set-fallback]', [])].filter(
        (product) =>
          product.id !== anchor.id &&
          product.available &&
          product.single &&
          product.variant_id &&
          Number(product.price) <= limit &&
          !hasAnyTag(product.tags, excluded)
      );
      const sameBrand = candidates.filter((product) => product.vendor === anchor.vendor);
      const others = candidates.filter((product) => product.vendor !== anchor.vendor);
      const picks = [];
      const types = new Set([anchor.type || '']);
      const ids = new Set([anchor.id]);
      [...sameBrand, ...others].forEach((product) => {
        if (picks.length === 2 || ids.has(product.id) || types.has(product.type || '')) return;
        picks.push(product);
        types.add(product.type || '');
        ids.add(product.id);
      });
      if (picks.length && this.isConnected) this.render([{ ...anchor, single: true }, ...picks]);
    }

    render(items) {
      const pct = tierFor(items.length);
      if (!pct) return;
      const full = items.reduce((sum, item) => sum + Number(item.price), 0);
      const total = full - Math.round((full * pct) / 100);
      this.querySelector('[data-set-items]').replaceChildren(
        ...items.map((item, index) => {
          const image = sizedImage(item.image, 160);
          return el('div', { class: 'av-set__item' }, [
            el('a', { href: item.url, class: 'av-set__thumb', tabindex: '-1', 'aria-hidden': 'true' }, [
              image ? el('img', { src: image, alt: '', width: '64', height: '64', loading: 'lazy' }) : null
            ]),
            el('div', { class: 'av-set__meta' }, [
              el('small', { text: index === 0 ? strings.routineThisItem : item.vendor || '' }),
              el('a', { href: item.url, text: shortTitle(item.title, item.vendor) })
            ]),
            el('span', { class: 'av-set__price', text: formatMoney(item.price) })
          ]);
        })
      );
      this.querySelector('[data-set-save]').textContent = fill(strings.routineSave, { percent: pct });
      const button = this.querySelector('[data-set-add]');
      const setLabel = () => {
        button.replaceChildren(
          el('span', { text: fill(strings.routineAddAll, { count: items.length, price: formatMoney(total) }) }),
          el('s', { text: formatMoney(full) })
        );
      };
      setLabel();
      button.onclick = async () => {
        button.disabled = true;
        try {
          await addToCart(items.map((item) => ({ id: item.variant_id, quantity: 1 })));
          button.replaceChildren(el('span', { text: strings.routineAdded }));
          setTimeout(setLabel, 1800);
        } catch (error) {
          window.alert(error.message);
        } finally {
          button.disabled = false;
        }
      };
      this.hidden = false;
    }
  }

  if (!customElements.get('av-complete-set')) customElements.define('av-complete-set', CompleteSet);

  /* ---------- Cart upsells ---------- */
  class CartUpsells extends HTMLElement {
    connectedCallback() {
      if (!config.upsells?.on) return;
      this.row = this.querySelector('[data-upsell-row]');
      this.inCart = (this.dataset.productIds || '').split(',').filter(Boolean).map(Number);
      this.load();
    }

    async load() {
      const lists = await Promise.all(this.inCart.slice(0, 3).map((id) => recommendationsFor(id)));
      const seen = new Set(this.inCart);
      const picks = [];
      const maxPrice = Number(config.upsells.maxPrice) || Infinity;
      [...lists.flat(), ...parseJSON(this, '[data-upsell-fallback]', [])].forEach((raw) => {
        const product = normaliseProduct(raw);
        if (picks.length >= config.upsells.count || seen.has(product.id)) return;
        if (!product.available || !product.single || !product.variant_id || Number(product.price) > maxPrice) return;
        seen.add(product.id);
        picks.push(product);
      });
      if (!picks.length || !this.isConnected) return;
      this.row.replaceChildren(...picks.map((product) => this.card(product)));
      this.hidden = false;
    }

    card(product) {
      const image = sizedImage(product.image, 140);
      const button = el('button', {
        type: 'button',
        class: 'av-upsell__add',
        text: strings.upsellAdd,
        'aria-label': fill(strings.upsellAddLabel, { title: product.title })
      });
      button.addEventListener('click', async () => {
        button.disabled = true;
        try {
          await addToCart([{ id: product.variant_id, quantity: 1 }]);
        } catch (error) {
          button.disabled = false;
          window.alert(error.message);
        }
      });
      return el('div', { class: 'av-upsell' }, [
        el('a', { href: product.url, class: 'av-upsell__media', tabindex: '-1', 'aria-hidden': 'true' }, [
          image ? el('img', { src: image, alt: '', width: '56', height: '56', loading: 'lazy' }) : null
        ]),
        el('div', { class: 'av-upsell__meta' }, [
          el('small', { text: product.vendor || '' }),
          el('a', { href: product.url, text: shortTitle(product.title, product.vendor) }),
          el('b', { text: formatMoney(product.price) })
        ]),
        button
      ]);
    }
  }

  if (!customElements.get('av-cart-upsells')) customElements.define('av-cart-upsells', CartUpsells);
})();
